<template>
  <acv-banner
    v-if="eulaReviewBannerVisible && eulaBannerContent"
    variant="informational"
    class="eula-review-banner"
  >
    <span class="eula-banner-content">{{ eulaBannerContent }}</span>

    <template #additional-checks>
      <div class="actions">
        <acv-button
          variant="link"
          class="actions__button dismiss__action"
          @click="onDismiss"
        >
          Dismiss
        </acv-button>

        <acv-button
          variant="link"
          class="actions__button review__action"
          @click="onReviewEula"
        >
          Review EULA
        </acv-button>
      </div>
    </template>
  </acv-banner>
</template>

<script setup>
import {
  computed, watch, getCurrentInstance,
} from 'vue';
import { AcvBanner, AcvButton } from '@acv/shared-vuejs';
import useAcvStore from '@/composables/useAcvStore';
import useAcvFeatureFlags from '@/composables/useAcvFeatureFlags';
import useAcvAnalytics from '@/composables/useAcvAnalytics';
import EulaMixin from '@/mixins/EulaMixin';
import useAcvAnnouncements from '@/composables/useAcvAnnouncements';

const instance = getCurrentInstance();

const props = defineProps({
  configuredLanes: {
    type: Array,
    default: () => [],
  },
});

const store = useAcvStore();
const acvFeatureFlags = useAcvFeatureFlags();
const analytics = useAcvAnalytics();

const isLaneEulaAcceptanceRequired = acvFeatureFlags.getFlag('requireEulaAcceptanceForLanes');
const eulaReviewBannerVisible = computed(() => store.getters['marketplaceView/eulaReviewBannerVisible']);

// Computed
const eulaNotAcceptedLanes = computed(() => props.configuredLanes.filter(
  (lane) => lane.eulaAccepted === false,
));

const eulaBannerContent = computed(() => {
  if (!isLaneEulaAcceptanceRequired.value) {
    return '';
  }

  if (eulaNotAcceptedLanes.value.length === 1) {
    const lane = eulaNotAcceptedLanes.value[0];

    return `Your account has been granted access to the ${lane.displayName} Private Marketplace Listing. 
    Please review and accept the End User License Agreement (EULA) to place bids on vehicles in this Listing.`;
  }

  if (eulaNotAcceptedLanes.value.length > 1) {
    return `Your account has been granted access to ${eulaNotAcceptedLanes.value.length} Private Marketplace Listings. 
    Please review and accept the End User License Agreement (EULA) to place bids on vehicles in these Listings.`;
  }

  return '';
});

// Watchers
watch(eulaNotAcceptedLanes, (newVal, oldVal) => {
  if (newVal.length !== oldVal.length) {
    store.dispatch('auctionCommonModals/updateAdditionalProperties', {
      configuredLanes: newVal,
    });
  }
});

// Methods
const onDismiss = () => {
  analytics.track(
    analytics.eventNames.marketplaceV2.eulaBannerDismissed,
    {
      lane_id: eulaNotAcceptedLanes.value.length === 1 ? eulaNotAcceptedLanes.value[0].id : null,
      num_of_eulas_to_review: eulaNotAcceptedLanes.value.length,
    },
  );
  store.dispatch('marketplaceView/updateEulaReviewBannerVisibility', false);

  useAcvAnnouncements().removeAnnouncementByKey('eula-review-banner');
};

const onReviewEula = () => {
  EulaMixin.methods.openEulaModal.bind(instance.proxy)({
    configuredLanes: eulaNotAcceptedLanes.value,
    originPoint: 'srp',
    intercepted: false,
    additionalProperties: {
      dismissible: true,
    },
  });

  analytics.track(
    analytics.eventNames.marketplaceV2.eulaBannerClicked,
    {
      lane_id: eulaNotAcceptedLanes.value.length === 1 ? eulaNotAcceptedLanes.value[0].id : null,
      num_of_eulas_to_review: eulaNotAcceptedLanes.value.length,
    },
  );
};
</script>

<style lang="scss" scoped>
.eula-review-banner {
  display: flex;

  @screen tw-lg {
    flex-direction: row;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
  }

  :deep(.acv-banner__icon) {
    align-self: center;
  }

  .actions {
    grid-area: actions;
    justify-self: end;
    display: flex;
    flex-flow: row nowrap;
    column-gap: 1rem;
    align-self: flex-end;

    @screen tw-lg {
      align-self: flex-start;
    }

    &__button {
      min-width: min-content;
      padding: 1rem;
      white-space: nowrap;
    }
  }
}
</style>
